import type { ReactiveController, ReactiveControllerHost } from 'lit';
import { uploadCommentAttachment } from 'Api/comments';
import { VideoCommentEvents, extractFileExtension, trackVideoCommentEvent } from 'Analytics/video-comment-events';
import {
    MAX_FILE_BYTES,
    MAX_FILE_COUNT,
    isAcceptedType,
    type RejectionReason,
} from './vm-video-comment-composer.helpers';

/** Tracks a single in-flight or completed upload. `id` is `undefined` while
 * the request is pending, set to the server-assigned id on success. `aborter`
 * lets us cancel the request if the user removes the chip mid-flight. */
export interface UploadRecord {
    id?: number;
    aborter: AbortController;
}

interface CommentAttachmentsControllerOptions {
    /** Resolved lazily on each upload so a slug change on the host propagates
     * without needing to re-bind the controller. */
    videoSlug: () => string;
}

/**
 * Owns a comment composer's file-pick / upload state machine on behalf of
 * its host: the picked files list, one `UploadRecord` per file (in-flight
 * abort handle + eventual server id), a blob URL cache for chip thumbnails,
 * the sticky "upload-failed modal was shown for this batch" flag, and the
 * currently-active `rejection` reason (validation or upload).
 *
 * The host owns rendering (chip strip, rejection modal) and reads through
 * `files`, `hasUploadInFlight`, `uploadOf`, `urlFor`, and `rejection`.
 * Mutations happen through `add`, `remove`, `clear`, and `clearRejection`;
 * each mutation calls `host.requestUpdate()` so the host's template stays in
 * sync.
 *
 * `hostDisconnected` aborts in-flight uploads and revokes cached blob URLs,
 * but leaves `_files` / `_uploads` intact. Reattach is not supported today —
 * the composer is always torn down with its host — so an aborted-then-
 * reattached instance would render chips with `id === undefined` and
 * `hasUploadInFlight` stuck at `true`. Add a full teardown here if that
 * ever changes.
 */
export class CommentAttachmentsController implements ReactiveController {
    private _host: ReactiveControllerHost;
    private _options: CommentAttachmentsControllerOptions;

    private _files: File[] = [];
    private _uploads = new Map<File, UploadRecord>();
    private _fileUrls = new WeakMap<File, string>();
    /** Currently-active rejection reason, or `null` when the modal is closed.
     * Host reads via `rejection`, clears via `clearRejection()`. */
    private _rejection: RejectionReason | null = null;
    /** Sticky flag preventing the upload-failed modal from re-opening every
     * time a subsequent file in the same pick batch fails. Reset on the
     * next accepted pick. */
    private _uploadFailureShown = false;

    constructor(host: ReactiveControllerHost, options: CommentAttachmentsControllerOptions) {
        this._host = host;
        this._options = options;
        host.addController(this);
    }

    /** Live reference to accepted-and-attached files, in pick order. Not a
     * defensive copy — callers must not mutate. */
    get files(): readonly File[] {
        return this._files;
    }

    /** True while any picked file's upload is still in flight (its record
     * has no resolved `id` yet). */
    get hasUploadInFlight(): boolean {
        for (const record of this._uploads.values()) {
            if (record.id === undefined) return true;
        }
        return false;
    }

    /** Currently-active rejection reason, or `null` when nothing to show. */
    get rejection(): RejectionReason | null {
        return this._rejection;
    }

    uploadOf(file: File): UploadRecord | undefined {
        return this._uploads.get(file);
    }

    /** Cached blob URL for a chip thumbnail — same object across renders so
     * `<img src>` doesn't churn. */
    urlFor(file: File): string {
        let url = this._fileUrls.get(file);
        if (!url) {
            url = URL.createObjectURL(file);
            this._fileUrls.set(file, url);
        }
        return url;
    }

    /**
     * Validate a freshly-picked batch and, if accepted, extend `files` and
     * kick off uploads in parallel. Returns `true` when the batch was
     * accepted, `false` on rejection (host uses the return to decide
     * follow-up UI like refocusing the textarea).
     */
    add(files: File[]): boolean {
        const badType = files.filter((f) => !isAcceptedType(f));
        if (badType.length) {
            this._reject('type', badType);
            return false;
        }

        const oversize = files.filter((f) => f.size > MAX_FILE_BYTES);
        if (oversize.length) {
            this._reject('per_file_size', oversize);
            return false;
        }

        if (this._files.length + files.length > MAX_FILE_COUNT) {
            this._reject('count', files);
            return false;
        }

        // Accepted — attach chips immediately and kick off uploads in parallel.
        // ATTEMPTED fires here (pick-time) rather than at submit; SUCCEEDED is
        // implied by absence of a subsequent FAILED for the same file.
        // Reset the upload-failed modal's "already shown" flag for the new batch.
        this._uploadFailureShown = false;
        this._files = [...this._files, ...files];
        trackVideoCommentEvent(VideoCommentEvents.ATTACHMENT_UPLOAD_ATTEMPTED, {
            files: files.map((f) => ({
                extension: extractFileExtension(f.name),
                file_size: f.size,
            })),
        });
        files.forEach((f) => this._startUpload(f));
        this._host.requestUpdate();
        return true;
    }

    /** Drop a single file: abort its in-flight upload, revoke its blob URL,
     * and remove it from the list. */
    remove(file: File): void {
        this._removeAttachment(file);
        this._host.requestUpdate();
    }

    /** Reset all attachment state (post-submit). Aborts in-flight uploads,
     * revokes cached blob URLs, empties the list. */
    clear(): void {
        this._files.forEach((file) => this._disposeFile(file));
        this._files = [];
        this._uploads = new Map();
        this._rejection = null;
        this._uploadFailureShown = false;
        this._host.requestUpdate();
    }

    /** Close the currently-open rejection modal. Used by the host's cancel /
     * retry handlers. */
    clearRejection(): void {
        if (this._rejection === null) return;
        this._rejection = null;
        this._host.requestUpdate();
    }

    hostDisconnected(): void {
        // Abort in-flight uploads + revoke blob URLs. The list stays in state
        // (element is going away anyway); the dispose primitive handles both
        // in-flight aborts and URL revocation so any future per-file cleanup
        // lands here automatically.
        this._files.forEach((file) => this._disposeFile(file));
    }

    /** Kick off an upload for a freshly-picked file. Records the AbortController
     * so `remove` can cancel; on failure, drops the chip and opens the
     * `'upload_failed'` rejection modal. */
    private _startUpload(file: File): void {
        const aborter = new AbortController();
        // Reassign the Map (not in-place `.set`) so the host's next render
        // sees the new reference.
        this._uploads = new Map(this._uploads).set(file, { aborter });
        uploadCommentAttachment({ videoSlug: this._options.videoSlug(), file, signal: aborter.signal })
            .then((response) => {
                const record = this._uploads.get(file);
                // Chip may have been removed before the response arrived.
                if (!record) return;
                this._uploads = new Map(this._uploads).set(file, { ...record, id: response.id });
                this._host.requestUpdate();
            })
            .catch(() => {
                // Abort is intentional — swallow silently.
                if (aborter.signal.aborted) return;
                if (!this._uploads.has(file)) return;
                this._onUploadFailed(file);
            });
    }

    /** Drops a failed upload's chip, fires ATTACHMENT_UPLOAD_FAILED, and
     * opens `'upload_failed'` if it's the first failure of the batch and
     * no other rejection is already open. */
    private _onUploadFailed(file: File): void {
        this._removeAttachment(file);
        trackVideoCommentEvent(VideoCommentEvents.ATTACHMENT_UPLOAD_FAILED, {
            files: [{ extension: extractFileExtension(file.name), file_size: file.size }],
        });
        // Only open the modal on the first failure of the batch, and never
        // clobber an already-open client-side rejection (type / per_file_size /
        // count). Subsequent failures in the same pick still fire the FAILED
        // analytics event and still drop their chip — they just don't re-nag.
        if (!this._uploadFailureShown && this._rejection === null) {
            this._uploadFailureShown = true;
            this._rejection = 'upload_failed';
        }
        this._host.requestUpdate();
    }

    private _reject(reason: Exclude<RejectionReason, 'upload_failed'>, files: File[]): void {
        trackVideoCommentEvent(VideoCommentEvents.ATTACHMENT_UPLOAD_REJECTED, {
            files: files.map((f) => ({
                extension: extractFileExtension(f.name),
                file_size: f.size,
                reason,
            })),
        });
        this._rejection = reason;
        this._host.requestUpdate();
    }

    /** Full single-file removal: cleanup + drop from `_uploads` and `_files`
     * (both reassigned so the host re-renders). Used by `remove` (user
     * clicks ✕) and `_onUploadFailed` (upload rejected). */
    private _removeAttachment(file: File): void {
        this._disposeFile(file);
        const uploads = new Map(this._uploads);
        uploads.delete(file);
        this._uploads = uploads;
        this._files = this._files.filter((f) => f !== file);
    }

    /** Per-file resource cleanup: aborts any in-flight upload and revokes the
     * cached blob URL. Does NOT touch `_uploads` / `_files` membership —
     * callers update those (immutably) themselves. */
    private _disposeFile(file: File): void {
        this._uploads.get(file)?.aborter.abort();
        const url = this._fileUrls.get(file);
        if (url) URL.revokeObjectURL(url);
        this._fileUrls.delete(file);
    }
}
